"use client";

import { useEffect } from 'react';
import { cleanupDialogOverlays } from '@/lib/utils/dialog-cleanup';

/**
 * Global Dialog Cleanup Provider
 * 
 * This component provides a safety net for dialog overlays that might not
 * properly clean up due to animation timing or state management issues.
 * It monitors the DOM for closed dialogs and ensures they don't block interactions.
 */
export function DialogCleanupProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    let cleanupTimeout: NodeJS.Timeout;

    // Observer for dialog state changes
    const observer = new MutationObserver((mutations) => {
      // Debounce cleanup to avoid excessive calls
      clearTimeout(cleanupTimeout);
      cleanupTimeout = setTimeout(() => {
        cleanupDialogOverlays();
      }, 400);
    });

    // Portals mount as direct children of body. cleanupDialogOverlays only
    // restores body pointer-events / overflow, so watching the body itself
    // (childList + style) is enough — subtree:true was firing on every
    // dashboard DOM mutation (chat, lists, sheets).
    observer.observe(document.body, {
      childList: true,
      subtree: false,
      attributes: true,
      attributeFilter: ['style'],
    });

    // Periodic safety check (every 5 seconds) to catch any stuck overlays
    const safetyInterval = setInterval(() => {
      const closedOverlays = document.querySelectorAll(
        '[data-radix-dialog-overlay][data-state="closed"], [data-radix-alert-dialog-overlay][data-state="closed"]'
      );
      
      if (closedOverlays.length > 0) {
        cleanupDialogOverlays();
      }
    }, 5000);

    // Also listen for route changes (in case dialogs are left open during navigation)
    const handleRouteChange = () => {
      setTimeout(cleanupDialogOverlays, 100);
    };

    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      observer.disconnect();
      clearTimeout(cleanupTimeout);
      clearInterval(safetyInterval);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  return <>{children}</>;
}

